import { type ReactNode } from "react";

import { isNullOrWhitespace } from "utilities/string/string";

import { EntityApprovalsViewerPresentational } from "commonComponents/entity/EntityApprovals/EntityApprovalsViewerPresentational";
import SignatureDisplay from "commonComponents/entity/signatureDisplay/SignatureDisplay";

import { SignatureRequestUserStatus } from "handlers";

import { type ProposalForDisplay } from "entity/estimate/common/FormattedProposal/FormattedProposal.types";

interface IDisclaimerTextContentProps {
    disclaimerText: string;
    className?: string;
}

/**
 * Base component for rendering disclaimer text with dangerouslySetInnerHTML
 * Returns null if disclaimerText is empty
 */
export const DisclaimerTextContent = ({
    disclaimerText,
    className,
}: IDisclaimerTextContentProps) => {
    if (isNullOrWhitespace(disclaimerText)) {
        return null;
    }

    return (
        <div
            className={className}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: disclaimerText }}
        />
    );
};

/**
 * Checks if the proposal has no legacy signatures (signaturePath is empty for all signatures).
 * When true, the proposal can use Nutrient signing experience.
 */
export const hasNoLegacySignatures = (proposal: ProposalForDisplay): boolean => {
    return proposal.signatures.every((sig) => isNullOrWhitespace(sig.signaturePath));
};

/**
 * Checks if the proposal was signed via Nutrient and if users have taken any
 * approval actions (signed, declined, etc.).
 */
export const hasNutrientApprovalActions = (proposal: ProposalForDisplay): boolean => {
    // Do not show approvals component if we have a legacy signature path
    if (!hasNoLegacySignatures(proposal)) {
        return false;
    }

    // Check if userApprovals exists and has items
    if (!proposal.userApprovals || proposal.userApprovals.length === 0) {
        return false;
    }

    // Check if anyone has taken an action
    return (
        proposal.userApprovals.filter(
            (sig) =>
                sig.status !== SignatureRequestUserStatus.Unsigned &&
                sig.status !== SignatureRequestUserStatus.NotApplicable,
        ).length > 0
    );
};

interface ISignatureSectionProps {
    proposalData: ProposalForDisplay;
    signatureFieldsToDisplayCount: number;
    lazyLoadImages: boolean;
    disclaimer: ReactNode;
}

/**
 * Shared signature section component
 * Conditionally renders either the approvals viewer or signature displays
 *
 * NOTE: Using EntityApprovalsViewerPresentational instead of EntityApprovalsViewer
 *
 * Why this approach:
 *   • Need to conditionally show/hide based on internal approval data (shouldShowApprovalsComponent)
 *
 * Component selection guideline:
 *   • DEFAULT: Use EntityApprovalsViewer (generic and self-contained)
 *   • EXCEPTION: Use EntityApprovalsViewerPresentational when you need to examine
 *                approval data to decide whether to show it
 */
export const SignatureSection = ({
    proposalData,
    lazyLoadImages,
    signatureFieldsToDisplayCount,
    disclaimer,
}: ISignatureSectionProps) => {
    const showApprovalsComponent = hasNutrientApprovalActions(proposalData);
    return (
        <div>
            {disclaimer}
            {showApprovalsComponent ? (
                <EntityApprovalsViewerPresentational
                    canViewAgreement={false}
                    onViewAgreement={() => {}}
                    users={proposalData.userApprovals ?? []}
                />
            ) : (
                <>
                    {Array.from({ length: signatureFieldsToDisplayCount }, (_, index) => (
                        <SignatureDisplay
                            key={`signatureDisplay_${index}`}
                            approvalStatusType="pending"
                            lazyLoad={lazyLoadImages}
                        />
                    ))}
                </>
            )}
        </div>
    );
};
